import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowRight, Clock, ImageIcon, Pencil, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getProgram, createCourse, updateCourse, lmsGetMeLearner, lmsGetEnrollments, getTutorAssignedCourses, type ProgramWithCourses, type Course, type CreateCoursePayload, type UpdateCoursePayload } from '@/lib/api';
import { getCardOverview } from '@/data/courseCopy';
import { useAuth } from '@/contexts/AuthContext';
import { useCurrency } from '@/contexts/CurrencyContext';
import { CourseCardSkeletonGrid } from '@/components/skeletons/CourseCardSkeleton';
import { SlideOver } from '@/components/SlideOver';
import { CourseForm } from '@/components/admin/CourseForm';

function CourseCard({
  course,
  programSlug,
  isAdmin,
  onEdit,
  isEnrolled,
}: { course: Course; programSlug: string; isAdmin?: boolean; onEdit?: (course: Course) => void; isEnrolled?: boolean }) {
  const { formatKesPrice } = useCurrency();
  const overview = (course.card_overview && course.card_overview.trim()) ? course.card_overview : getCardOverview(programSlug, course.slug);
  const [imgFailed, setImgFailed] = useState(false);
  useEffect(() => setImgFailed(false), [course.image_url]);
  const hasImage = (course.image_url && course.image_url.trim()) && !imgFailed;
  return (
    <div className="rounded-2xl bg-card border border-border overflow-hidden hover:shadow-lg transition-all flex flex-col h-full relative">
      {isAdmin && onEdit && (
        <button
          type="button"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onEdit(course); }}
          className="absolute top-4 right-4 z-10 p-2 rounded-lg bg-background/80 border border-border hover:bg-muted text-muted-foreground hover:text-foreground"
          aria-label="Edit course"
        >
          <Pencil className="w-4 h-4" />
        </button>
      )}
      <div className="aspect-video w-full bg-muted/50 flex-shrink-0 overflow-hidden">
        {hasImage ? (
          <img
            src={course.image_url!}
            alt=""
            className="w-full h-full object-cover"
            onError={() => setImgFailed(true)}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-muted-foreground/50">
            <ImageIcon className="w-12 h-12" aria-hidden />
          </div>
        )}
      </div>
      <div className="p-6 flex-1 flex flex-col">
        <h3 className="font-display font-bold text-lg text-foreground mb-2">{course.title}</h3>
        {overview ? (
          <p className="text-sm text-muted-foreground leading-relaxed mb-4 line-clamp-4">{overview}</p>
        ) : null}
        <div className="flex flex-wrap gap-2 mb-4">
          {course.duration ? (
            <span className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full bg-africa-blue/10 text-africa-blue border border-africa-blue/20">
              <Clock className="w-3.5 h-3.5" /> {course.duration}
            </span>
          ) : null}
          {!isEnrolled && (
            <span className="inline-flex items-center text-xs font-semibold px-2.5 py-1 rounded-full bg-africa-green/10 text-africa-green border border-africa-green/20">
              {formatKesPrice(course.price_kes)}
            </span>
          )}
          <span className="inline-flex items-center text-xs font-medium px-2.5 py-1 rounded-full bg-africa-purple/10 text-africa-purple border border-africa-purple/20">
            Course
          </span>
        </div>
        <div className="mt-auto pt-4">
          <Button size="sm" asChild className="w-full sm:w-auto bg-accent text-accent-foreground hover:bg-gold-light rounded-lg gap-1">
            <Link to={`/courses/${programSlug}/courses/${course.slug}`}>
              {isEnrolled ? 'Continue' : 'View course details'} <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </Button>
        </div>
      </div>
    </div>
  );
}

export function ProgramDetailPage() {
  const { programSlug } = useParams<{ programSlug: string }>();
  const [program, setProgram] = useState<ProgramWithCourses | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const isTutor = user?.role === 'tutor';
  const [coursePanelOpen, setCoursePanelOpen] = useState(false);
  const [editingCourse, setEditingCourse] = useState<Course | null>(null);
  const [enrolledCourseIds, setEnrolledCourseIds] = useState<Set<string>>(new Set());
  const [tutorAssignedCourseIds, setTutorAssignedCourseIds] = useState<Set<string>>(new Set());
  const [tutorAssignedProgramSlugs, setTutorAssignedProgramSlugs] = useState<Set<string>>(new Set());

  const refetchProgram = () => {
    if (!programSlug) return;
    getProgram(programSlug).then(setProgram).catch(() => {});
  };

  useEffect(() => {
    if (!programSlug) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    getProgram(programSlug)
      .then((p) => { if (!cancelled) setProgram(p); })
      .catch((err) => { if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load program'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [programSlug]);

  // Enrolled course IDs for this program (signed-in user) — reorder and show Continue
  useEffect(() => {
    if (!user || user.role === 'admin' || !programSlug) {
      setEnrolledCourseIds(new Set());
      return;
    }
    let cancelled = false;
    lmsGetMeLearner()
      .then(({ learnerId }) => (learnerId ? lmsGetEnrollments(learnerId) : { enrollments: [] }))
      .then(({ enrollments: list }) => {
        if (cancelled) return;
        const ids = new Set(
          list.filter((e) => e.course.programSlug === programSlug).map((e) => e.courseId)
        );
        setEnrolledCourseIds(ids);
      })
      .catch(() => { if (!cancelled) setEnrolledCourseIds(new Set()); });
    return () => { cancelled = true; };
  }, [user?.id, user?.role, programSlug]);

  // Tutor: fetch assigned course ids and program slugs (for edit button and "Back to dashboard")
  useEffect(() => {
    if (!isTutor) return;
    getTutorAssignedCourses()
      .then((data) => {
        setTutorAssignedCourseIds(new Set((data.courses ?? []).map((c) => c.courseId)));
        setTutorAssignedProgramSlugs(new Set((data.programs ?? []).map((p) => p.programSlug)));
      })
      .catch(() => {
        setTutorAssignedCourseIds(new Set());
        setTutorAssignedProgramSlugs(new Set());
      });
  }, [isTutor]);

  if (!programSlug) {
    return (
      <div className="max-w-7xl mx-auto px-6 py-12">
        <p className="text-destructive">Invalid program.</p>
        <Link to="/courses" className="text-accent hover:underline mt-2 inline-block">Back to programs</Link>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-6 py-12">
        <nav className="text-sm text-muted-foreground mb-6">
          <span className="h-4 w-16 bg-muted rounded inline-block animate-pulse" />
        </nav>
        <div className="h-9 w-3/4 max-w-xl bg-muted rounded mb-4 animate-pulse" />
        <div className="h-5 w-48 bg-muted rounded mb-8 animate-pulse" />
        <CourseCardSkeletonGrid count={6} />
      </div>
    );
  }

  if (error || !program) {
    return (
      <div className="max-w-7xl mx-auto px-6 py-12">
        <p className="text-destructive">{error ?? 'Program not found.'}</p>
        <Link to="/courses" className="text-accent hover:underline mt-2 inline-block">Back to programs</Link>
      </div>
    );
  }

  const canEditCourse = (courseId: string) =>
    isAdmin || (isTutor && (tutorAssignedCourseIds.has(courseId) || (program != null && tutorAssignedProgramSlugs.has(program.slug))));

  return (
    <div className="max-w-7xl mx-auto px-6 py-12">
      <nav className="text-sm text-muted-foreground mb-6">
        {isTutor ? (
          <Link to="/tutor/dashboard" className="hover:text-foreground">Dashboard</Link>
        ) : (
          <Link to="/courses" className="hover:text-foreground">Programs</Link>
        )}
        <span className="mx-2">/</span>
        <span className="text-foreground">{program.title}</span>
      </nav>
      <h1 className="text-3xl md:text-4xl font-display font-bold text-foreground mb-2">{program.title}</h1>
      {program.category_name ? (
        <p className="text-muted-foreground mb-8">Category: {program.category_name}</p>
      ) : null}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <p className="text-muted-foreground">
          {program.courses.length} {program.courses.length === 1 ? 'course' : 'courses'} in this program.
        </p>
        {(isAdmin || (isTutor && (tutorAssignedCourseIds.size > 0 || tutorAssignedProgramSlugs.size > 0))) && (
          <>
            {isAdmin && (
              <Button
                size="sm"
                className="bg-accent text-accent-foreground hover:bg-gold-light gap-1"
                onClick={() => { setEditingCourse(null); setCoursePanelOpen(true); }}
              >
                <Plus className="w-4 h-4" /> Add course
              </Button>
            )}
          </>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {[...program.courses]
          .sort((a, b) => {
            const aEn = enrolledCourseIds.has(a.id);
            const bEn = enrolledCourseIds.has(b.id);
            if (aEn && !bEn) return -1;
            if (!aEn && bEn) return 1;
            return 0;
          })
          .map((course) => (
            <CourseCard
              key={course.id}
              course={course}
              programSlug={program.slug}
              isAdmin={canEditCourse(course.id)}
              onEdit={canEditCourse(course.id) ? (c) => { setEditingCourse(c); setCoursePanelOpen(true); } : undefined}
              isEnrolled={enrolledCourseIds.has(course.id)}
            />
          ))}
      </div>

      <SlideOver
        open={coursePanelOpen}
        onClose={() => { setCoursePanelOpen(false); setEditingCourse(null); }}
        title={editingCourse ? 'Edit course' : 'Add course'}
      >
        <CourseForm
          initial={editingCourse}
          onSave={async (payload) => {
            if (!programSlug) return;
            if (editingCourse) {
              await updateCourse(programSlug, editingCourse.slug, payload as UpdateCoursePayload);
            } else {
              await createCourse(programSlug, payload as CreateCoursePayload);
            }
            setCoursePanelOpen(false);
            setEditingCourse(null);
            refetchProgram();
          }}
          onCancel={() => { setCoursePanelOpen(false); setEditingCourse(null); }}
        />
      </SlideOver>

      <div className="mt-10">
        {isTutor ? (
          <Link to="/tutor/dashboard" className="text-accent hover:underline font-medium">← Back to dashboard</Link>
        ) : (
          <Link to="/courses" className="text-accent hover:underline font-medium">← Back to all courses</Link>
        )}
      </div>
    </div>
  );
}
